import type { CmmdWorkIntake } from "@cmmd-forge/contracts";
import {
  CMMD_WORK_INTAKE_FRESH_MS,
  CMMD_WORK_INTAKE_MAX_STALE_MS,
} from "@cmmd-forge/shared/cmmdWorkIntakeCachePolicy";

const CMMD_WORK_INTAKE_CACHE_KEY = "forge:cmmd-work-intake-cache:v1";
export const CMMD_WORK_INTAKE_EVICTION_KEY = "forge:cmmd-work-intake-eviction:v1";
export const CMMD_WORK_INTAKE_PROJECT_EVICTION_PREFIX =
  "forge:cmmd-work-intake-project-eviction:v1:";
const CMMD_WORK_INTAKE_CACHE_VERSION = 1 as const;
const CMMD_WORK_INTAKE_EVICTION_VERSION = 1 as const;

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem"> &
  Partial<Pick<Storage, "key" | "length">>;

export interface CachedCmmdWorkIntakeRecord {
  readonly version: typeof CMMD_WORK_INTAKE_CACHE_VERSION;
  readonly cachedAt: string;
  readonly intake: CmmdWorkIntake;
  /** Shared cross-tab fence captured when this record was written. */
  readonly evictionGeneration?: string | null;
}

interface CmmdWorkIntakeEviction {
  readonly version: typeof CMMD_WORK_INTAKE_EVICTION_VERSION;
  readonly generation: string;
  readonly projectIds: readonly string[];
}

interface CmmdWorkIntakeProjectEviction {
  readonly version: typeof CMMD_WORK_INTAKE_EVICTION_VERSION;
  readonly generation: string;
  readonly projectId: string;
}

export type CmmdWorkIntakeEvictionStorageChange =
  | { readonly kind: "cache_removed" }
  | { readonly kind: "projects_removed"; readonly projectIds: readonly string[] }
  | { readonly kind: "malformed" }
  | { readonly kind: "unrelated" };

export interface CachedCmmdWorkIntakeLookup {
  readonly state: "fresh" | "stale" | "expired" | "missing";
  readonly record: CachedCmmdWorkIntakeRecord | null;
}

function resolveStorage(): StorageLike | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object";
}

function isOptionalString(value: unknown): value is string | null | undefined {
  return value === undefined || value === null || typeof value === "string";
}

function parseCachedRecord(raw: string | null): CachedCmmdWorkIntakeRecord | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<CachedCmmdWorkIntakeRecord>;
    if (parsed.version !== CMMD_WORK_INTAKE_CACHE_VERSION) return null;
    if (typeof parsed.cachedAt !== "string") return null;
    if (!isRecord(parsed.intake)) return null;
    if (!isOptionalString(parsed.evictionGeneration)) return null;
    return parsed as CachedCmmdWorkIntakeRecord;
  } catch {
    return null;
  }
}

function normalizeEvictedProjectIds(parsed: Partial<CmmdWorkIntakeEviction>): readonly string[] {
  const projectIds = Array.isArray(parsed.projectIds)
    ? parsed.projectIds.filter(
        (projectId): projectId is string => typeof projectId === "string" && projectId.length > 0,
      )
    : [];
  const legacyProjectId = (parsed as { readonly projectId?: unknown }).projectId;
  const candidates =
    projectIds.length === 0 && typeof legacyProjectId === "string" && legacyProjectId.length > 0
      ? [legacyProjectId]
      : projectIds;
  return [...new Set(candidates)];
}

function parseCmmdWorkIntakeEviction(raw: string | null): CmmdWorkIntakeEviction | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<CmmdWorkIntakeEviction>;
    if (parsed.version !== CMMD_WORK_INTAKE_EVICTION_VERSION) return null;
    if (typeof parsed.generation !== "string" || parsed.generation.length === 0) return null;
    const projectIds = normalizeEvictedProjectIds(parsed);
    if (projectIds.length === 0) return null;
    return {
      version: CMMD_WORK_INTAKE_EVICTION_VERSION,
      generation: parsed.generation,
      projectIds: [...new Set(projectIds)],
    };
  } catch {
    return null;
  }
}

function projectEvictionKey(projectId: string): string {
  return `${CMMD_WORK_INTAKE_PROJECT_EVICTION_PREFIX}${encodeURIComponent(projectId)}`;
}

function parseCmmdWorkIntakeProjectEviction(
  raw: string | null,
): CmmdWorkIntakeProjectEviction | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<CmmdWorkIntakeProjectEviction>;
    if (parsed.version !== CMMD_WORK_INTAKE_EVICTION_VERSION) return null;
    if (typeof parsed.generation !== "string" || parsed.generation.length === 0) return null;
    if (typeof parsed.projectId !== "string" || parsed.projectId.length === 0) return null;
    return parsed as CmmdWorkIntakeProjectEviction;
  } catch {
    return null;
  }
}

export function parseCmmdWorkIntakeEvictionStorageChange(
  key: string | null,
  newValue: string | null,
): CmmdWorkIntakeEvictionStorageChange {
  if (key === CMMD_WORK_INTAKE_CACHE_KEY && newValue === null) {
    return { kind: "cache_removed" };
  }
  if (key === CMMD_WORK_INTAKE_EVICTION_KEY) {
    const eviction = parseCmmdWorkIntakeEviction(newValue);
    return eviction
      ? { kind: "projects_removed", projectIds: eviction.projectIds }
      : { kind: "malformed" };
  }
  if (key?.startsWith(CMMD_WORK_INTAKE_PROJECT_EVICTION_PREFIX)) {
    const eviction = parseCmmdWorkIntakeProjectEviction(newValue);
    return eviction
      ? { kind: "projects_removed", projectIds: [eviction.projectId] }
      : { kind: "malformed" };
  }
  return { kind: "unrelated" };
}

function readCmmdWorkIntakeEvictedProjectIds(storage: StorageLike): readonly string[] {
  const projectIds = new Set(
    parseCmmdWorkIntakeEviction(storage.getItem(CMMD_WORK_INTAKE_EVICTION_KEY))?.projectIds ?? [],
  );
  if (typeof storage.length === "number" && typeof storage.key === "function") {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (!key?.startsWith(CMMD_WORK_INTAKE_PROJECT_EVICTION_PREFIX)) continue;
      const tombstone = parseCmmdWorkIntakeProjectEviction(storage.getItem(key));
      if (tombstone) projectIds.add(tombstone.projectId);
    }
  }
  return [...projectIds].toSorted();
}

function filterCmmdWorkIntakeByPersistedEvictions(
  intake: CmmdWorkIntake,
  storage: StorageLike | null = resolveStorage(),
): CmmdWorkIntake {
  if (!storage) return intake;
  try {
    const projectIds = new Set(readCmmdWorkIntakeEvictedProjectIds(storage));
    if (projectIds.size === 0) return intake;
    return {
      ...intake,
      projects: intake.projects.filter((project) => !projectIds.has(project.id)),
      tasks: intake.tasks.filter(
        (task) => task.projectId === null || !projectIds.has(task.projectId),
      ),
    };
  } catch {
    return intake;
  }
}

export function readCmmdWorkIntakeEvictionGeneration(
  storage: StorageLike | null = resolveStorage(),
): string | null {
  if (!storage) return null;
  try {
    const eviction = parseCmmdWorkIntakeEviction(storage.getItem(CMMD_WORK_INTAKE_EVICTION_KEY));
    const projectIds = readCmmdWorkIntakeEvictedProjectIds(storage);
    if (!eviction && projectIds.length === 0) return null;
    return `${eviction?.generation ?? "project-tombstones"}:${projectIds.join(",")}`;
  } catch {
    return null;
  }
}

/**
 * A server snapshot is authoritative for projects it contains. Remove only
 * those project tombstones so a project that still exists in CMMD can be
 * imported again after its Forge execution workspace was removed.
 */
export function clearPersistedProjectEvictions(
  projectIds: readonly string[],
  storage: StorageLike | null = resolveStorage(),
): void {
  if (!storage || projectIds.length === 0) return;
  try {
    const idsToRestore = new Set(projectIds);
    const remainingIds = readCmmdWorkIntakeEvictedProjectIds(storage).filter(
      (projectId) => !idsToRestore.has(projectId),
    );
    for (const projectId of idsToRestore) {
      storage.removeItem(projectEvictionKey(projectId));
    }
    if (remainingIds.length === 0) {
      storage.removeItem(CMMD_WORK_INTAKE_EVICTION_KEY);
    } else {
      storage.setItem(
        CMMD_WORK_INTAKE_EVICTION_KEY,
        JSON.stringify({
          version: CMMD_WORK_INTAKE_EVICTION_VERSION,
          generation: createEvictionGeneration(),
          projectIds: remainingIds,
        } satisfies CmmdWorkIntakeEviction),
      );
    }
  } catch {
    // Cache recovery is best effort; the in-memory authoritative snapshot is
    // still correct for this session.
  }
}

function createEvictionGeneration(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export function readCachedCmmdWorkIntake(
  nowMs: number = Date.now(),
  storage: StorageLike | null = resolveStorage(),
): CachedCmmdWorkIntakeLookup {
  if (!storage) {
    return { state: "missing", record: null };
  }

  const persistedRecord = parseCachedRecord(storage.getItem(CMMD_WORK_INTAKE_CACHE_KEY));
  if (!persistedRecord) {
    return { state: "missing", record: null };
  }
  if (
    (persistedRecord.evictionGeneration ?? null) !== readCmmdWorkIntakeEvictionGeneration(storage)
  ) {
    return { state: "missing", record: null };
  }
  const record: CachedCmmdWorkIntakeRecord = {
    ...persistedRecord,
    // Defense in depth: even a cache payload written by an older tab or an
    // interleaved writer cannot hydrate a per-project tombstone.
    intake: filterCmmdWorkIntakeByPersistedEvictions(persistedRecord.intake, storage),
  };

  const cachedAtMs = Date.parse(record.cachedAt);
  if (!Number.isFinite(cachedAtMs)) {
    return { state: "expired", record: null };
  }

  const ageMs = Math.max(0, nowMs - cachedAtMs);
  if (ageMs <= CMMD_WORK_INTAKE_FRESH_MS) {
    return { state: "fresh", record };
  }
  if (ageMs <= CMMD_WORK_INTAKE_MAX_STALE_MS) {
    return { state: "stale", record };
  }
  return { state: "expired", record: null };
}

export function writeCachedCmmdWorkIntake(
  intake: CmmdWorkIntake,
  now: Date = new Date(),
  storage: StorageLike | null = resolveStorage(),
): void {
  const expectedGeneration = readCmmdWorkIntakeEvictionGeneration(storage);
  writeCachedCmmdWorkIntakeIfGenerationCurrent(intake, expectedGeneration, now, storage);
}

export function writeCachedCmmdWorkIntakeIfGenerationCurrent(
  intake: CmmdWorkIntake,
  expectedGeneration: string | null,
  now: Date = new Date(),
  storage: StorageLike | null = resolveStorage(),
): boolean {
  if (!storage) {
    return false;
  }
  if (readCmmdWorkIntakeEvictionGeneration(storage) !== expectedGeneration) return false;

  const payload: CachedCmmdWorkIntakeRecord = {
    version: CMMD_WORK_INTAKE_CACHE_VERSION,
    cachedAt: now.toISOString(),
    intake: filterCmmdWorkIntakeByPersistedEvictions(intake, storage),
    evictionGeneration: expectedGeneration,
  };

  try {
    storage.setItem(CMMD_WORK_INTAKE_CACHE_KEY, JSON.stringify(payload));
    if (readCmmdWorkIntakeEvictionGeneration(storage) !== expectedGeneration) {
      // A permanent deletion raced this write from another tab. Removing the
      // record is safe even if it also discards that tab's filtered rewrite:
      // a cache miss refetches, while a stale record can resurrect data.
      storage.removeItem(CMMD_WORK_INTAKE_CACHE_KEY);
      return false;
    }
    return true;
  } catch {
    // Best-effort cache only.
    return false;
  }
}

export function removeProjectFromCachedCmmdWorkIntake(
  projectId: string,
  storage: StorageLike | null = resolveStorage(),
): void {
  if (!storage) return;
  try {
    const existingProjectIds = readCmmdWorkIntakeEvictedProjectIds(storage);
    const projectEviction: CmmdWorkIntakeProjectEviction = {
      version: CMMD_WORK_INTAKE_EVICTION_VERSION,
      generation: createEvictionGeneration(),
      projectId,
    };
    for (const existingProjectId of existingProjectIds) {
      const existingKey = projectEvictionKey(existingProjectId);
      if (storage.getItem(existingKey)) continue;
      storage.setItem(
        existingKey,
        JSON.stringify({
          version: CMMD_WORK_INTAKE_EVICTION_VERSION,
          generation: createEvictionGeneration(),
          projectId: existingProjectId,
        } satisfies CmmdWorkIntakeProjectEviction),
      );
    }
    // Distinct project keys make concurrent different-project deletions
    // non-overwriting. Publish this immutable tombstone before the aggregate
    // notification record or any cache rewrite.
    storage.setItem(projectEvictionKey(projectId), JSON.stringify(projectEviction));
    const eviction: CmmdWorkIntakeEviction = {
      version: CMMD_WORK_INTAKE_EVICTION_VERSION,
      generation: createEvictionGeneration(),
      projectIds: readCmmdWorkIntakeEvictedProjectIds(storage),
    };
    // Publish the cross-tab fence before reading or rewriting the shared cache.
    // Every in-flight sync that captured the older generation must now stop.
    storage.setItem(CMMD_WORK_INTAKE_EVICTION_KEY, JSON.stringify(eviction));
    const evictionGeneration = readCmmdWorkIntakeEvictionGeneration(storage);
    const record = parseCachedRecord(storage.getItem(CMMD_WORK_INTAKE_CACHE_KEY));
    if (!record) return;
    storage.setItem(
      CMMD_WORK_INTAKE_CACHE_KEY,
      JSON.stringify({
        ...record,
        evictionGeneration,
        intake: filterCmmdWorkIntakeByPersistedEvictions(record.intake, storage),
      } satisfies CachedCmmdWorkIntakeRecord),
    );
    if (readCmmdWorkIntakeEvictionGeneration(storage) !== evictionGeneration) {
      storage.removeItem(CMMD_WORK_INTAKE_CACHE_KEY);
    }
  } catch {
    // Fail closed: a record that could not be rewritten must not survive to
    // resurrect the deleted project on reload. Storage itself may be
    // unavailable, so the fallback invalidation remains independently guarded.
    try {
      storage.removeItem(CMMD_WORK_INTAKE_CACHE_KEY);
    } catch {
      // The in-memory eviction remains authoritative for this session and the
      // requested network refresh repairs cache when storage becomes writable.
    }
  }
}

export function clearCachedCmmdWorkIntake(storage: StorageLike | null = resolveStorage()): void {
  if (!storage) {
    return;
  }
  try {
    storage.removeItem(CMMD_WORK_INTAKE_CACHE_KEY);
  } catch {
    // Best-effort cache only.
  }
}
